import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { TransitionGroup, CSSTransition } from 'react-transition-group';
import { getExternalUrl } from '../utils/externalUrl';
import Modal from '../components/Modal';
// import './RecommendationsPage.css';

const RecommendationsPage = ({ apiBaseUrl }) => {
  const { isAuthenticated, userRoles } = useAuth();
  const navigate = useNavigate();
  const [recommendations, setRecommendations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState({ isOpen: false, title: '', message: '', buttons: [], icon: null });

  useEffect(() => {
    if (!isAuthenticated || !userRoles.includes('manage-media')) {
      console.log('Unauthorized access to recommendations');
      navigate('/');
      return;
    }

    const fetchRecommendations = async () => {
      try {
        const res = await fetch(`${apiBaseUrl}/recommendations/full`, {
          credentials: 'include',
        });
        if (res.status === 403) {
          console.log('Forbidden: Insufficient permissions');
          navigate('/');
          return;
        }
        const data = await res.json();
        console.log('Fetched full recommendations:', JSON.stringify(data, null, 2));
        setRecommendations(data);
      } catch (err) {
        console.error('Error fetching recommendations:', err);
        setModal({
          isOpen: true,
          title: 'Error',
          message: 'Failed to load recommendations',
          buttons: [{ label: 'OK', class: 'btn btn-primary', action: () => {} }],
          icon: <img src="/static/icons/circle-xmark-solid.svg" alt="Error" className="modal-icon" />,
        });
      } finally {
        setLoading(false);
      }
    };

    fetchRecommendations();
  }, [apiBaseUrl, isAuthenticated, userRoles, navigate]);

  const showModal = (title, message, buttons, icon = null) => {
    console.log('Opening modal with icon:', icon ? icon.props : null);
    setModal({ isOpen: true, title, message, buttons, icon });
  };

  const closeModal = () => setModal({ ...modal, isOpen: false });

  const handleAccept = (rec) => {
    showModal(
      'Accept Recommendation',
      `Are you sure you want to accept "${rec.title}"?`,
      [
        {
          label: 'Yes',
          class: 'btn btn-primary',
          action: async () => {
            try {
              const res = await fetch(`${apiBaseUrl}/add/${rec.media_type.toLowerCase()}/${rec.external_id}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({
                  title: rec.title,
                  image: rec.data.image || '',
                }),
              });
              const data = await res.json();
              if (data.success) {
                setRecommendations(prev => prev.filter(r => r.id !== rec.id));
                showModal(
                  'Success',
                  'Media added successfully!',
                  [{ label: 'OK', class: 'btn btn-primary', action: () => {} }],
                  <img src="/static/icons/circle-check-solid.svg" alt="Success" className="modal-icon" />
                );
              } else {
                showModal(
                  'Error',
                  data.error || 'Failed to add media',
                  [{ label: 'OK', class: 'btn btn-primary', action: () => {} }],
                  <img src={data.icon || '/static/icons/circle-xmark-solid.svg'} alt="Error" className="modal-icon" />
                );
                console.error('Accept error:', data);
              }
            } catch (err) {
              showModal(
                'Error',
                'Failed to add media',
                [{ label: 'OK', class: 'btn btn-primary', action: () => {} }],
                <img src="/static/icons/circle-xmark-solid.svg" alt="Error" className="modal-icon" />
              );
              console.error('Accept error:', err);
            }
          },
        },
        { label: 'Cancel', class: 'btn btn-secondary', action: () => {} },
      ],
      <img src="/static/icons/circle-question-solid.svg" alt="Confirm" className="modal-icon" />
    );
  };

  const handleReject = (recId) => {
    showModal(
      'Reject Recommendation',
      'Are you sure you want to reject this recommendation?',
      [
        {
          label: 'Yes',
          class: 'btn btn-primary',
          action: async () => {
            try {
              const res = await fetch(`${apiBaseUrl}/recommendations/reject/${recId}`, {
                method: 'POST',
                credentials: 'include',
              });
              const data = await res.json();
              if (data.success) {
                setRecommendations(prev => prev.filter(r => r.id !== recId));
                showModal(
                  'Success',
                  'Recommendation rejected',
                  [{ label: 'OK', class: 'btn btn-primary', action: () => {} }],
                  <img src="/static/icons/circle-check-solid.svg" alt="Success" className="modal-icon" />
                );
              } else {
                showModal(
                  'Error',
                  'Failed to reject recommendation',
                  [{ label: 'OK', class: 'btn btn-primary', action: () => {} }],
                  <img src="/static/icons/circle-xmark-solid.svg" alt="Error" className="modal-icon" />
                );
                console.error('Reject error:', data);
              }
            } catch (err) {
              showModal(
                'Error',
                'Failed to reject recommendation',
                [{ label: 'OK', class: 'btn btn-primary', action: () => {} }],
                <img src="/static/icons/circle-xmark-solid.svg" alt="Error" className="modal-icon" />
              );
              console.error('Reject error:', err);
            }
          },
        },
        { label: 'Cancel', class: 'btn btn-secondary', action: () => {} },
      ],
      <img src="/static/icons/circle-question-solid.svg" alt="Confirm" className="modal-icon" />
    );
  };

  return (
    <div className="home-container">
      <h1 className="recommend-title">📥 Pending Recommendations</h1>

      {loading && <p>Loading recommendations...</p>}

      {!loading && recommendations.length === 0 && (
        <p>No new recommendations at the moment ✨</p>
      )}

      {!loading && recommendations.length > 0 && (
        <TransitionGroup component="div" className="recommend-grid">
          {recommendations.map(rec => (
            <CSSTransition
              key={rec.id}
              timeout={500}
              classNames="recommend-card"
            >
              <div className="recommend-card" id={`rec-${rec.id}`}>
                <img
                  src={rec.data.image || '/static/images/placeholder.jpg'}
                  className="recommend-img"
                  alt={rec.title}
                />

                <div className="recommend-body">
                  <h3>{rec.title}</h3>
                  <p>
                    <small>
                      {rec.media_type} • {rec.submitted_by}
                    </small>
                  </p>

                  {rec.external_id && (
                    <a
                      href={getExternalUrl(rec.media_type, rec.external_id)}
                      className="recommend-link"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      🔗 More Info
                    </a>
                  )}
                </div>

                <div className="recommend-actions">
                  <button
                    className="btn btn-primary"
                    onClick={() => handleAccept(rec)}
                  >
                    <img src="/static/icons/check-solid.svg" className="icon" alt="Accept" /> Accept
                  </button>
                  <button
                    className="btn btn-secondary"
                    onClick={() => handleReject(rec.id)}
                  >
                    <img src="/static/icons/xmark-solid.svg" className="icon" alt="Reject" /> Reject
                  </button>
                </div>
              </div>
            </CSSTransition>
          ))}
        </TransitionGroup>
      )}

      <Modal
        isOpen={modal.isOpen}
        onClose={closeModal}
        title={modal.title}
        message={modal.message}
        buttons={modal.buttons}
        icon={modal.icon}
      />
    </div>
  );
};

export default RecommendationsPage;